import { includes } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';

import {
  ContextMenuButton,
  ContextMenuButtonProps,
} from '@/components/form/baseInputs/Button/ContextMenuButton';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Repeat01Icon } from '@/components/icons/Repeat01Icon';
import { DeleteMenuItemWithConfirmationModalProps } from '@/components/poppers/MenuPopper/DeleteMenuItemWithConfirmationModal';
import { FlyoutMenuItem } from '@/components/poppers/MenuPopper/FlyoutMenuItem';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getSectionOptions } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { EntityKind, EntityStage } from '@/types/schema';

import { getEntityTypeFromEntityKind } from '../../utils/getEntityTypeFromEntityKind';
import {
  ConvertEntityMenuItem,
  CONVERTIBLE_ENTITY_KINDS,
} from '../ConvertEntity/ConvertEntityMenuItem';
import { ConvertEntityModal } from '../ConvertEntity/ConvertEntityModal';
import { ConvertToActiveMenuItem } from '../ConvertToActiveMenuItem/ConvertToActiveMenuItem';
import { DeleteEntityMenuItem } from '../DeleteEntityMenuItem';
import { DuplicateEntityMenuItem } from '../DuplicateEntityMenuItem';
import { MergeEntityModal } from '../MergeEntity/MergeEntityModal';
import { canShowEntityMerge } from '../MergeEntity/MergeEntityModal.utils';
import { UnlinkFromIntegrationMenuItem } from '../UnlinkFromIntegrationMenuItem/UnlinkFromIntegrationMenuItem';
import { EntityActionsMenu_EntityFragment } from './EntityActionsMenu.generated';

const DEFAULT_DELETE_MODAL_BODY =
  'Deleting will remove this entity from all associated entities and remove all other associated data such as transactions.';

export interface EntityActionsMenuProps {
  entity: EntityActionsMenu_EntityFragment;
  householdId: string;
  /** Called after the entity is deleted — e.g. navigate away or refetch. */
  onAfterDelete: () => void;
  /** Called after a "simple" entity conversion completes. */
  onAfterConvert?: () => void;
  /** Opens the entity edit surface at the selected section. */
  onEdit?: (section: EditEntitySection) => void;
  /**
   * Called with the new entity id after duplication. When omitted,
   * DuplicateEntityMenuItem navigates to the new entity's detail page.
   */
  onAfterDuplicate?: (newEntityId: string) => void;
  /**
   * Extra menu items rendered after "Duplicate". Used by the entity overview
   * page to inject the AI "regenerate suggestions" action, which depends on
   * suggestion contexts that aren't available everywhere this menu is used.
   */
  additionalMenuItems?: ReactNode;
  /**
   * Props forwarded to the underlying ContextMenuButton (e.g. size/variant and
   * click handling for the estate accordion summary rows).
   */
  contextMenuButtonProps?: Partial<ContextMenuButtonProps>;
  /** Override the delete confirmation modal copy. */
  deleteModalProps?: DeleteMenuItemWithConfirmationModalProps['modalProps'];
}

/**
 * Shared context menu of entity-level actions (convert, duplicate, unlink,
 * delete, ...). Originally inlined on the entity overview page; extracted so
 * the same actions can be offered directly from the entity cards on the Estate
 * tabs.
 */
export function EntityActionsMenu({
  entity,
  householdId,
  onAfterDelete,
  onAfterConvert,
  onEdit,
  onAfterDuplicate,
  additionalMenuItems,
  contextMenuButtonProps,
  deleteModalProps,
}: EntityActionsMenuProps) {
  const canEdit = useCanEditHouseholdData();
  const canManageIntegrationData = useCanManageIntegrationData();
  const isMergeEnabled = useFeatureFlag('merge_client_profiles');
  const { primaryClients } = useHouseholdDetailsContext();
  const canDeleteEntity = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_DELETE_ENTITIES_AND_PROPOSALS,
    { detailed: false }
  );
  const [isConvertEntityModalOpen, setIsConvertEntityModalOpen] =
    useState(false);
  const [isMergeEntityModalOpen, setIsMergeEntityModalOpen] = useState(false);

  const { kind } = entity;
  const type = getEntityTypeFromEntityKind(kind);
  const editEntitySections = useMemo(
    () => getSectionOptions(type, primaryClients),
    [type, primaryClients]
  );

  const canUnlinkIntegration =
    canManageIntegrationData && Boolean(entity.integrationEntities?.length);
  if (!canEdit && !canUnlinkIntegration) return null;

  const isDraft = entity.stage === EntityStage.Draft;
  const isGrat = kind === EntityKind.GratTrust;
  const isConvertible = includes(CONVERTIBLE_ENTITY_KINDS, kind);

  return (
    <>
      <ContextMenuButton {...contextMenuButtonProps}>
        {canEdit && onEdit && editEntitySections.length > 0 && (
          <FlyoutMenuItem label="Edit entity" icon={<Edit02Icon size={16} />}>
            {editEntitySections.map(({ label, section }) => (
              <MenuItem
                key={section}
                label={label}
                onClick={() => onEdit(section)}
              />
            ))}
          </FlyoutMenuItem>
        )}
        {canEdit && isDraft && type && (
          <ConvertToActiveMenuItem
            entityId={entity.id}
            subtypeId={entity.subtype.id}
            entityType={type}
            entityDisplayName={entity.subtype.displayName}
          />
        )}
        {canEdit && !isGrat && (
          <DuplicateEntityMenuItem
            label="Duplicate"
            householdId={householdId}
            entityId={entity.id}
            icon={<Copy07Icon size={16} />}
            onAfterCreate={onAfterDuplicate}
          />
        )}
        {canEdit && additionalMenuItems}
        {canShowEntityMerge(
          canEdit,
          canDeleteEntity,
          isMergeEnabled,
          entity.stage
        ) && (
          <MenuItem
            label="Merge duplicate"
            icon={<Repeat01Icon size={16} />}
            onClick={() => setIsMergeEntityModalOpen(true)}
          />
        )}
        {canEdit && isConvertible && (
          <ConvertEntityMenuItem
            entityId={entity.id}
            entityKind={kind}
            setOpenConvertModal={setIsConvertEntityModalOpen}
            onAfterConvert={onAfterConvert}
            icon={<Repeat01Icon size={16} />}
          />
        )}
        {canUnlinkIntegration && (
          <UnlinkFromIntegrationMenuItem entity={entity} />
        )}
        {canEdit && canDeleteEntity && <SelectItemGroupLabel />}
        {canEdit && canDeleteEntity && (
          <DeleteEntityMenuItem
            entityId={entity.id}
            modalProps={{
              children: DEFAULT_DELETE_MODAL_BODY,
              ...deleteModalProps,
            }}
            onDelete={onAfterDelete}
          />
        )}
      </ContextMenuButton>
      {isConvertEntityModalOpen && (
        <ConvertEntityModal
          entityId={entity.id}
          entityKind={kind}
          isOpen={isConvertEntityModalOpen}
          onClose={() => setIsConvertEntityModalOpen(false)}
          onAfterConvert={onAfterConvert}
        />
      )}
      {isMergeEntityModalOpen && (
        <MergeEntityModal
          entityId={entity.id}
          entityKind={kind}
          entityName={entity.subtype.displayName}
          householdId={householdId}
          onClose={() => setIsMergeEntityModalOpen(false)}
        />
      )}
    </>
  );
}
